import { useRef, useState } from 'react';
import { FileCheck, UploadCloud, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BotonPegarArchivos, usePegadoArchivos } from '@/components/ui/pegado-archivos';

/**
 * Zona de carga de archivo: arrastrar, hacer clic o pegar, con el archivo elegido a la vista.
 *
 * Pegar (Ctrl/Cmd+V) funciona sobre la zona o en cualquier parte del modal que la contiene, y
 * el botón "Pegar del portapapeles" cubre el teléfono. El arrastre también se acepta en todo el
 * modal. Es el estándar de `components/ui/pegado-archivos`.
 *
 * Reemplaza al `<input type="file">` crudo, que en la práctica es un botón gris con la leyenda
 * "No se ha seleccionado ningún archivo": no admite arrastrar, no dice qué formatos acepta hasta
 * que falla, y una vez elegido el archivo no hay forma de quitarlo sin cerrar el modal.
 *
 * Valida antes de entregar el archivo -extensión y tamaño- porque el error tiene que salir donde
 * está el usuario, no después de mandar la subida.
 *
 * Es UI sin dominio: ya se repite en la carga de evidencia de pago, en documentos del expediente
 * y en materiales de proyecto, así que vive aquí.
 */

export interface FileDropzoneProps {
  value: File | null;
  onChange: (file: File | null) => void;
  /** Igual que el `accept` del input: '.pdf,.jpg,.png'. */
  accept?: string;
  /** Tamaño máximo en MB. */
  maxMb?: number;
  /** Texto de ayuda: qué se puede subir. */
  hint?: string;
  disabled?: boolean;
  className?: string;
  /** Muestra el botón "Pegar del portapapeles" y su aviso (por defecto sí). Ctrl/Cmd+V funciona siempre. */
  botonPegar?: boolean;
  /** Escuchar pegado y arrastre en todo el modal que la contiene (por defecto sí). */
  enModal?: boolean;
}

const kb = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

function extensionValida(nombre: string, accept?: string): boolean {
  if (!accept) return true;
  const exts = accept.split(',').map(e => e.trim().toLowerCase()).filter(e => e.startsWith('.'));
  if (!exts.length) return true;
  const punto = nombre.lastIndexOf('.');
  return punto !== -1 && exts.includes(nombre.slice(punto).toLowerCase());
}

export function FileDropzone({
  value, onChange, accept, maxMb = 10, hint, disabled = false, className, botonPegar = true, enModal = true,
}: FileDropzoneProps) {
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const zona = useRef<HTMLDivElement | null>(null);
  const { arrastrando, zonaProps } = usePegadoArchivos(zona, files => {
    // Zona de UN archivo: si sueltan o pegan varios no se elige uno al azar, se avisa.
    if (files.length > 1) { setError(`Aquí va un solo archivo y llegaron ${files.length}`); return; }
    recibir(files[0]);
  }, { disabled, enModal });

  function recibir(file: File | null | undefined) {
    if (!file) return;
    if (!extensionValida(file.name, accept)) {
      setError(`Ese formato no se acepta${hint ? `: ${hint}` : ''}`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setError(`El archivo pesa ${kb(file.size)} y el máximo son ${maxMb} MB`);
      return;
    }
    setError(null);
    onChange(file);
  }

  function quitar(e: React.MouseEvent) {
    e.stopPropagation();
    setError(null);
    onChange(null);
    if (input.current) input.current.value = '';
  }

  return (
    <div className={className}>
      <div
        ref={zona}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && input.current?.click()}
        onKeyDown={e => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); }
        }}
        {...zonaProps}
        className={cn(
          'relative rounded-lg border-2 border-dashed transition-colors outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          disabled ? 'opacity-60 cursor-not-allowed border-border bg-muted/20'
            : arrastrando ? 'border-primary bg-primary/5 cursor-pointer'
            : value ? 'border-primary/40 bg-primary/5 cursor-pointer hover:border-primary/60'
            : 'border-border bg-muted/30 cursor-pointer hover:border-muted-foreground/40',
        )}
      >
        <input
          ref={input}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={e => recibir(e.target.files?.[0])}
          className="sr-only"
        />
        <div className="flex flex-col items-center justify-center gap-1.5 py-7 px-4 text-center">
          {value ? (
            <>
              <FileCheck className="size-7 text-primary" aria-hidden />
              <p className="text-[13px] font-medium text-foreground break-all">{value.name}</p>
              <p className="text-[11px] text-muted-foreground">{kb(value.size)} - clic para cambiar</p>
              {!disabled && (
                <button
                  type="button"
                  onClick={quitar}
                  className="absolute top-2 right-2 rounded-full p-1 text-muted-foreground hover:bg-background hover:text-foreground transition-colors"
                  aria-label="Quitar archivo"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              )}
            </>
          ) : (
            <>
              <UploadCloud className="size-7 text-muted-foreground" aria-hidden />
              <p className="text-[13px] font-medium text-foreground">Arrastra el archivo aquí</p>
              <p className="text-[11px] text-muted-foreground">
                o haz clic para seleccionar{hint ? ` - ${hint}` : ''}
              </p>
              {botonPegar && !disabled && (
                <>
                  <p className="text-[11px] text-muted-foreground">También puedes pegarlo con Ctrl/Cmd+V.</p>
                  <BotonPegarArchivos onFiles={files => recibir(files[0])} className="mt-1 h-7 text-[12px]" />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {error && <p className="mt-1.5 text-[11px] text-danger">{error}</p>}
    </div>
  );
}
